import { useCallback, useEffect, useMemo, useState } from "react";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { logEvent, logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { useConnectionAdapter } from "../../../services/ConnectionRegistryProvider";
import { useAuthState } from "../../../auth/AuthStateProvider";
import {
  uploadFileToOneDrive,
  uploadRawToOneDrive,
  fetchEmailMime,
  createReadOnlyLink,
  fetchDriveFolderChildrenByPath,
  fetchItemPreviewUrl,
} from "../../email/services/graph";
import { decodeJwtClaims } from "../../../auth/jwt";
import { useEngageWorkflow } from "../hooks/useEngageWorkflow";
import { useEngageClient } from "../hooks/useEngageClient";
import { useEngageDocument, type EngageDocHost } from "../hooks/useEngageDocument";
import { NbaDocIcon, DeckorateIcon, MailIcon, WarningIcon, CheckIcon, PaperclipIcon, ChecklistIcon } from "../../../shared/components/icons";
import { tagEmailSubmitted } from "../services/emailCategory";
import {
  useEngageRequests,
  type EngageRequestFilter,
  type EngageRequestRow,
} from "../hooks/useEngageRequests";
import type { EngageJob } from "../../../chat/services/workflowApi";
import { readJobTitle } from "../utils/jobFields";
import styles from "./EngageWorkflowPanel.module.css";

interface EngageWorkflowPanelProps {
  readonly onBack?: () => void;
  readonly userEmail?: string;
}

type View = "create" | "requests";

function getSiteValue(s: { site?: string }): string {
  return s.site ?? "";
}

function getSiteLabel(s: { site?: string; display?: string }): string {
  return s.display ?? s.site ?? "";
}

function getJobTypeLabel(t: { joB_TYPE: string; title?: string; joB_TYPE_NAME?: string }): string {
  return t.title ?? t.joB_TYPE_NAME ?? t.joB_TYPE;
}

function getJobTypePlaceholder(selectedSite: string, loadingJobTypes: boolean): string {
  if (selectedSite) {
    return loadingJobTypes ? "Loading job types…" : "Select a job type…";
  }
  return "Select a site first";
}

function getTitlePlaceholder(host: EngageDocHost | null): string {
  if (host === "word") return "Job title (auto-filled from document)";
  if (host === "powerpoint") return "Job title (auto-filled from presentation)";
  return "Brief title for the job";
}

function DocHostIcon({ host }: Readonly<{ host: EngageDocHost | null }>) {
  if (host === "word") return <NbaDocIcon />;
  if (host === "powerpoint") return <DeckorateIcon />;
  if (host === "outlook") return <MailIcon />;
  return null;
}

function attachmentSummary(saved: number): string {
  if (saved <= 0) return "";
  return ` — ${saved} attachment${saved === 1 ? "" : "s"} sent`;
}

/**
 * Colour class for a status badge — green for finished, blue for in-flight,
 * amber for everything else (cancelled/on-hold). Mirrors the prototype's
 * complete / active / pending split.
 */
function statusColorClass(text: string): string {
  const s = text.toLowerCase();
  if (/complet|deliver|closed|finish|\bdone\b/.test(s)) return styles.badgeComplete ?? "";
  if (/process|intake|progress|accept|start|active|revis|reopen|\bopen\b|queue|review/.test(s)) {
    return styles.badgeActive ?? "";
  }
  return styles.badgePending ?? "";
}

/** Title-case a raw status/action (e.g. "STARTED" → "Started"). */
function formatStatus(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Build a OneDrive uploader for the submit, or `undefined` when it needn't run
 * (no attachments). Engage's SaveAttachments only accepts a URL reference, so
 * EVERY attachment (Outlook binary or Word/PPT file) must first be staged in
 * OneDrive to obtain a URL — hence no host gate (AIVA-6989).
 *
 * The URL handed to Engage is a **read-only, org-scoped sharing link** (like the
 * Knowledge Base opens files view-only), NOT the raw drive `webUrl` — the raw
 * link downloads the file (and gets intercepted by MCAS as a "Blocked_…"
 * download), whereas the sharing link opens it in the browser's viewer. Falls
 * back to the raw `webUrl` only if the sharing link can't be minted.
 *
 * The Graph token is acquired LAZILY inside the returned uploader, not up front.
 * The uploader runs only while staging an attachment, which the hook does AFTER
 * the job is created. Acquiring the token here — before submit — meant that for
 * any document WITH content (a non-empty document always yields an attachment)
 * `refreshGraphToken` could open the interactive Office auth dialog BEFORE the
 * create call; that dialog disrupted the in-flight submit and the create returned
 * no job number, so a document with content failed while a blank one (no
 * attachment, so this returned early and never touched Graph) succeeded. Deferring
 * the token keeps job creation independent of Graph auth (AIVA-7885).
 */
function buildDriveUploader(
  attachments: ReadonlyArray<unknown>,
  graphToken: string | null,
  refreshGraphToken: () => Promise<string | null>,
):
  | ((
      folderPath: string,
      fileName: string,
      base64: string,
      contentType: string,
    ) => Promise<{ url: string; size: number }>)
  | undefined {
  if (attachments.length === 0) return undefined;
  return async (folderPath, fileName, base64, contentType) => {
    const token = graphToken ?? (await refreshGraphToken());
    if (!token) {
      // No Graph token → nowhere to host the file. Throw so the (already created)
      // job is reported with an attachment error, never a failed submit.
      throw new Error("No Graph token available to stage the attachment");
    }
    const item = await uploadFileToOneDrive(
      token,
      folderPath,
      fileName,
      base64,
      contentType,
      refreshGraphToken,
    );
    const viewUrl = await createReadOnlyLink(token, "/me/drive", item.id, refreshGraphToken);
    return { url: viewUrl ?? item.webUrl, size: item.size };
  };
}

/** Filename-safe timestamp (e.g. "2026-08-04 13-18-07") for the exported .eml. */
function emlTimestamp(): string {
  return new Date().toISOString().slice(0, 19).replace("T", " ").replace(/:/g, "-");
}

/**
 * Export the open email as a `.eml` into the job's OneDrive folder, mirroring
 * the prototype (AIVA-6990). Best-effort: no Graph token → no export.
 */
async function exportEmlToDrive(
  emailId: string,
  subject: string,
  jobNo: string,
  graphToken: string | null,
  refreshGraphToken: () => Promise<string | null>,
): Promise<void> {
  const token = graphToken ?? (await refreshGraphToken());
  if (!token) return;
  const eml = await fetchEmailMime(token, emailId, refreshGraphToken);
  if (eml.size === 0) return;
  const safe = (subject || "email").replace(/[^a-zA-Z0-9 .-]/g, " ").slice(0, 60).trim() || "email";
  const emlName = `${safe} ${emlTimestamp()}.eml`;
  await uploadRawToOneDrive(token, `AiVA/Engage/${jobNo}`, emlName, eml, "message/rfc822", refreshGraphToken);
}

/**
 * The signed-in Engage COGNITO identity (email, else username) decoded from the
 * connection's id token — NOT the M365/Office account, which can differ. Used
 * as the default requestor and to scope My Requests → Completed to this user.
 */
function resolveCognitoEmail(adapter: ReturnType<typeof useConnectionAdapter>): string {
  if (!adapter?.isConnected()) return "";
  const idToken = adapter.getAuthHeaders()["id"] ?? "";
  if (!idToken) return "";
  const claims = decodeJwtClaims(idToken);
  return (
    (claims.email as string | undefined) ??
    (claims["cognito:username"] as string | undefined) ??
    ""
  );
}

// Today's local date as a "YYYY-MM-DD" string for the due-date input's `min`,
// so the picker discourages past dates (AIVA-6847). Uses local parts rather
// than toISOString() to avoid an off-by-one near midnight in non-UTC zones.
function todayInputValue(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function EngageWorkflowPanel({ onBack, userEmail }: Readonly<EngageWorkflowPanelProps>) {
  const [view, setView] = useState<View>("create");

  return (
    <div className={styles.panel}>
      <ModuleHeader
        title="Engage Workflow"
        subtitle="Submit & track jobs"
        onBack={onBack}
      />

      <div className={styles.tabBar} role="tablist" aria-label="Engage views">
        {(["create", "requests"] as View[]).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={`${styles.tab} ${view === v ? styles.tabActive : ""}`}
            onClick={() => setView(v)}
          >
            {v === "create" ? "Create Job" : "My Requests"}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        {view === "create" ? (
          <CreateJobView userEmail={userEmail} />
        ) : (
          <MyRequestsView userEmail={userEmail} />
        )}
      </div>
    </div>
  );
}

/* ── Create Job View ─────────────────────────── */

interface CreateJobViewProps {
  readonly userEmail?: string;
}

function CreateJobView({ userEmail }: CreateJobViewProps) {
  const {
    sites,
    selectedSite,
    setSelectedSite,
    jobTypes,
    loadingSites,
    loadingJobTypes,
    submitting,
    error,
    submitJob,
    reloadSites,
  } = useEngageWorkflow();

  const adapter = useConnectionAdapter("engage");
  const doc = useEngageDocument();
  // Graph token to stage email attachments in OneDrive for FILEURL sites
  // (AIVA-6989). Best-effort — when unavailable, submit falls back to the
  // inline SaveAttachments path.
  const { graphToken, refreshGraphToken } = useAuthState();

  // Default the requestor to the signed-in Engage COGNITO user (decoded from
  // the connection's id token), not the M365/Office account — the two differ.
  const cognitoEmail = useMemo(() => resolveCognitoEmail(adapter), [adapter]);

  const [jobType, setJobType] = useState("");
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [checkedAtt, setCheckedAtt] = useState<Record<string, boolean>>({});
  const [lastResult, setLastResult] = useState<{
    jobNo: string;
    attachmentsSaved: number;
    attachmentError: boolean;
  } | null>(null);

  // Prefill the job title from the open document/email until the user edits it.
  // Mirror the source even when it has no title (e.g. an email with no subject)
  // so the previously selected email's subject doesn't linger as stale data
  // (AIVA-6842). Gated on `doc.ready` so we never clear during startup.
  useEffect(() => {
    if (titleEdited || !doc.ready) return;
    setTitle(doc.suggestedTitle);
  }, [doc.ready, doc.suggestedTitle, titleEdited]);

  // Default every document attachment to checked when the set changes.
  const attKey = doc.attachments.map((a) => a.id).join(",");
  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const a of doc.attachments) next[a.id] = true;
    setCheckedAtt(next);
  }, [attKey]);

  const canSubmit =
    !submitting &&
    selectedSite.length > 0 &&
    jobType.length > 0 &&
    title.trim().length > 0 &&
    dueDate.length > 0;

  async function collectAttachments() {
    const out: Array<{ name: string; text?: string; base64?: string; mimeType?: string }> = [];
    for (const a of doc.attachments) {
      if (!checkedAtt[a.id]) continue;
      try {
        if (a.getBytes) {
          // Binary attachment (e.g. an email's file attachment, AIVA-6989) —
          // pass the raw base64 bytes through with the real content type so
          // they aren't double-encoded. Skip only when the bytes can't be read.
          const base64 = await a.getBytes();
          if (base64) out.push({ name: a.name, base64, mimeType: a.mimeType });
        } else if (a.getText) {
          // Text attachment (Word/PowerPoint export) — encoded in the hook.
          // Send whatever text was extracted — even empty — so a checked
          // attachment always results in a SaveAttachments call.
          const text = await a.getText();
          out.push({ name: a.name, text });
        }
      } catch {
        // Only skip when the content genuinely can't be read; submit continues.
      }
    }
    return out;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLastResult(null);
    try {
      const attachments = await collectAttachments();
      // Build the request body once so we can emit the paramCount telemetry
      // straight off it — no drift between what we log and what we send.
      const submitBody = {
        site: selectedSite,
        jobType,
        title: title.trim(),
        instructions,
        dueDate: dueDate || undefined,
        // Requestor resolves from the signed-in Engage Cognito user (falls back
        // to the M365 email inside the hook). Not shown in the UI.
        requestorEmail: cognitoEmail || undefined,
        userEmail,
        // SaveAttachments records the poster as the Engage Cognito user, not the
        // M365 account.
        postedByEmail: cognitoEmail || undefined,
        attachments,
      };
      logEvent("workflow.submit", "usage", {
        connectorSystemName: "engage",
        paramCount: Object.keys(submitBody).length,
      });
      // Build the OneDrive uploader that stages attachments so SaveAttachments
      // can register them by URL (AIVA-6989). The uploader acquires its Graph
      // token lazily and the hook invokes it only AFTER the job is created, so a
      // Graph auth dialog can never sit between submit and job creation
      // (AIVA-7885). Best-effort: with no Graph token the attachments can't be
      // hosted, so the hook reports an attachment error on an already-created job.
      const uploadToDrive = buildDriveUploader(attachments, graphToken, refreshGraphToken);
      const result = await submitJob({ ...submitBody, uploadToDrive });
      setLastResult({
        jobNo: result.jobNo,
        attachmentsSaved: result.attachmentsSaved,
        attachmentError: result.attachmentError,
      });
      // Tag the source email with its job number (as the prototype did) so the
      // user can see in their inbox which emails became jobs, and which job.
      // Outlook-only and best-effort — never blocks submit.
      if (doc.host === "outlook") {
        tagEmailSubmitted(`Engage #${result.jobNo}`).catch(() => undefined);
        // Export the email itself as a .eml into the job's OneDrive folder
        // (AIVA-6990). Best-effort — never blocks or fails submit.
        if (doc.emailId) {
          exportEmlToDrive(
            doc.emailId,
            doc.suggestedTitle,
            result.jobNo,
            graphToken,
            refreshGraphToken,
          ).catch(() => undefined);
        }
      }
      setTitle("");
      setTitleEdited(false);
      setInstructions("");
      setDueDate("");
    } catch {
      // error state is already set by the hook
    }
  }

  const titlePlaceholder = getTitlePlaceholder(doc.host);

  return (
    <>
      {doc.host &&
        (doc.empty ? (
          <div className={styles.docCard}>
            <span className={styles.docIcon} aria-hidden>
              <DocHostIcon host={doc.host} />
            </span>
            <p className={styles.docEmpty}>{doc.emptyMessage}</p>
          </div>
        ) : (
          <div className={styles.docCard}>
            <div className={styles.docPrimary}>
              <span className={styles.docPrimaryIcon} aria-hidden>
                <DocHostIcon host={doc.host} />
              </span>{" "}
              {doc.primary}
            </div>
            <div className={styles.docSecondary}>{doc.secondary}</div>
            <div className={styles.docCaption}>{doc.caption}</div>
          </div>
        ))}

      <form className={styles.card} onSubmit={onSubmit}>
      <div className={styles.row}>
        <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="eng-site">
          Site
        </label>
        <select
          id="eng-site"
          className={styles.select}
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
          disabled={loadingSites}
          required
          aria-required="true"
        >
          <option value="">{loadingSites ? "Loading sites…" : "Select a site"}</option>
          {sites.map((s) => {
            const value = getSiteValue(s);
            const label = getSiteLabel(s);
            return (
              <option key={value} value={value}>{label}</option>
            );
          })}
        </select>
      </div>

      <div className={styles.row}>
        <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="eng-jobtype">
          Job type
        </label>
        <select
          id="eng-jobtype"
          className={styles.select}
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          disabled={!selectedSite || loadingJobTypes}
          required
          aria-required="true"
        >
          <option value="">
            {getJobTypePlaceholder(selectedSite, loadingJobTypes)}
          </option>
          {jobTypes.map((t) => (
            <option key={t.joB_TYPE} value={t.joB_TYPE}>{getJobTypeLabel(t)}</option>
          ))}
        </select>
      </div>

      <div className={styles.row}>
        <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="eng-title">
          Job title
        </label>
        <input
          id="eng-title"
          className={styles.input}
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setTitleEdited(true);
          }}
          placeholder={titlePlaceholder}
          required
          aria-required="true"
        />
      </div>

      <div className={styles.row}>
        <label className={styles.label} htmlFor="eng-instr">Instructions</label>
        <textarea
          id="eng-instr"
          className={styles.textarea}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Optional notes or instructions"
        />
      </div>

      <div className={styles.row}>
        <label className={`${styles.label} ${styles.labelRequired}`} htmlFor="eng-due">
          Due date
        </label>
        <input
          id="eng-due"
          className={styles.input}
          type="date"
          min={todayInputValue()}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          required
          aria-required="true"
        />
      </div>

      {doc.attachments.length > 0 && (
        <div className={styles.row}>
          <span className={styles.label}>Attachments</span>
          <div className={styles.attList}>
            {doc.attachments.map((a) => (
              <label key={a.id} className={styles.attRow}>
                <input
                  type="checkbox"
                  checked={checkedAtt[a.id] ?? false}
                  onChange={(e) =>
                    setCheckedAtt((prev) => ({ ...prev, [a.id]: e.target.checked }))
                  }
                />
                <span className={styles.attName}>{a.name}</span>
                <span className={styles.attCaption}>{a.caption}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorRow}>
          <p className={styles.error}>{error}</p>
          <button
            type="button"
            className={styles.retry}
            onClick={() => {
              reloadSites().catch(() => undefined);
            }}
            disabled={loadingSites}
          >
            {loadingSites ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}
      {lastResult && (
        <p className={styles.success}>
          Created job {lastResult.jobNo}
          {attachmentSummary(lastResult.attachmentsSaved)}.
        </p>
      )}
      {lastResult?.attachmentError && (
        <p className={styles.error}>
          Job created, but the attachment could not be uploaded.
        </p>
      )}

      <div className={styles.actions}>
        <button type="submit" className={styles.submit} disabled={!canSubmit}>
          {submitting ? "Submitting…" : "Submit Job"}
        </button>
      </div>
      </form>
    </>
  );
}

/* ── My Requests View ─────────────────────────── */

const REQUEST_FILTERS: ReadonlyArray<{ id: EngageRequestFilter; label: string }> = [
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
];

function MyRequestsView({ userEmail }: { readonly userEmail?: string }) {
  // The selected site lives in sessionStorage (aiva_engage_site) and drives the
  // requests call. We read it via useEngageWorkflow so the My Requests view
  // stays in sync with the Create Job site selector.
  const { selectedSite } = useEngageWorkflow();
  // Scope the (site-wide) completed feed to this user. The feed's createD_BY is
  // the Engage username, so prefer the Cognito identity over the M365 account.
  const adapter = useConnectionAdapter("engage");
  const cognitoEmail = useMemo(() => resolveCognitoEmail(adapter), [adapter]);
  const { rows, filter, setFilter, search, setSearch, loading, error, reload } =
    useEngageRequests({ site: selectedSite, userEmail: cognitoEmail || userEmail });
  // Track the opened job's number AND its list-row title. GetJob does not return
  // the job title (only the active feed does), so we carry the row's title into
  // the detail view as the source of truth for the heading (AIVA-6987).
  const [detail, setDetail] = useState<{ jobNo: string; title: string } | null>(null);

  function renderEmpty() {
    if (error) {
      const isTimeout = error.includes("504") || error.toLowerCase().includes("timeout");
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden><WarningIcon /></div>
          <p className={styles.emptyTitle}>
            {isTimeout ? "Connection timed out" : "Unable to load requests"}
          </p>
          <p className={styles.emptyMsg}>
            {isTimeout
              ? "The Engage server is taking longer than expected. Please try again."
              : "There was a problem connecting to Engage. Check your connection and try again."}
          </p>
          <button
            type="button"
            className={styles.retry}
            onClick={() => {
              reload().catch(() => undefined);
            }}
            disabled={loading}
          >
            {loading ? "Retrying…" : "↻ Retry"}
          </button>
        </div>
      );
    }
    const isCompleted = filter === "completed";
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon} aria-hidden>{isCompleted ? <CheckIcon /> : <ChecklistIcon />}</div>
        <p className={styles.emptyTitle}>
          {isCompleted ? "No completed requests" : "No open requests"}
        </p>
        <p className={styles.emptyMsg}>
          {isCompleted
            ? "Completed jobs will appear here once active jobs are finished."
            : "You have no jobs currently in progress. Create a new job to get started."}
        </p>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon} aria-hidden>🏢</div>
        <p className={styles.emptyTitle}>No site selected</p>
        <p className={styles.emptyMsg}>
          Choose a site on the Create Job tab to view your requests.
        </p>
      </div>
    );
  }

  if (detail) {
    return (
      <RequestDetailView
        site={selectedSite}
        jobNo={detail.jobNo}
        fallbackTitle={detail.title}
        onBack={() => setDetail(null)}
      />
    );
  }

  return (
    <>
      <input
        className={styles.searchInput}
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by job number or text…"
        aria-label="Search requests"
      />

      <div className={styles.filterBar} role="tablist" aria-label="Request status filter">
        {REQUEST_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`${styles.filterBtn} ${filter === f.id ? styles.filterBtnActive : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className={styles.loading}>Loading requests…</div>}

      {!loading && rows.length === 0 && renderEmpty()}

      {!loading && rows.length > 0 && (
        <div className={styles.reqList}>
          {rows.map((row) => (
            <RequestCard
              key={row.jobNo || row.title}
              row={row}
              onOpen={() => row.jobNo && setDetail({ jobNo: row.jobNo, title: row.title })}
            />
          ))}
        </div>
      )}
    </>
  );
}

function RequestCard({
  row,
  onOpen,
}: {
  readonly row: EngageRequestRow;
  readonly onOpen: () => void;
}) {
  return (
    <button type="button" className={styles.reqCard} onClick={onOpen}>
      {row.jobNo && <div className={styles.reqJobNo}>#{row.jobNo}</div>}
      <div className={styles.reqTitle}>{row.title}</div>
      {(row.requestor || row.dueDate) && (
        <div className={styles.reqMeta}>
          {row.requestor && <span>{row.requestor}</span>}
          {row.dueDate && <span>Due: {row.dueDate}</span>}
        </div>
      )}
    </button>
  );
}

/* ── Request Detail View ──────────────────────── */

interface DetailFieldSpec {
  readonly label: string;
  readonly keys: readonly string[];
  readonly date?: boolean;
  readonly dateOnly?: boolean; // date-only, no time component
  readonly full?: boolean;
}

// Field map + multi-casing key fallbacks, mirroring the prototype's
// showEngageRequestDetail.
const DETAIL_FIELDS: readonly DetailFieldSpec[] = [
  { label: "Requested By", keys: ["requesteD_BY", "REQUESTED_BY", "requestedBy", "requestor"] },
  { label: "Email", keys: ["email", "EMAIL", "requestorEmail"] },
  { label: "Due Date", keys: ["duE_DATE", "DUE_DATE", "dueDate"], dateOnly: true },
  { label: "Created Date", keys: ["createD_DATE", "CREATED_DATE", "createdDate", "dateCreated"], date: true },
  { label: "Job Type", keys: ["joB_TYPE", "JOB_TYPE", "jobType", "type"] },
  { label: "Priority", keys: ["priority", "PRIORITY", "urgency"] },
  { label: "Site", keys: ["site", "SITE", "siteName"] },
  { label: "Instructions", keys: ["instructions", "INSTRUCTIONS", "notes", "description", "joB_DESCRIPTION"], full: true },
  { label: "Assigned To", keys: ["assigneD_TO", "ASSIGNED_TO", "assignedTo", "operator"] },
  { label: "Department", keys: ["department", "DEPARTMENT", "dept"] },
  { label: "Cost Centre", keys: ["cosT_CENTRE", "COST_CENTRE", "costCentre", "costCenter"] },
  { label: "Reference", keys: ["reference", "REFERENCE", "ref", "externalRef"] },
];

const DETAIL_DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

// Date-only variant for fields the user never gives a time for (e.g. Due Date),
// mirroring the My Requests list view's DATE_FMT so no fabricated time is shown.
const DETAIL_DATE_ONLY_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function pickField(obj: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.length > 0) return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
  }
  return "";
}

function unwrapJob(resp: EngageJob | null): {
  job: Record<string, unknown> | null;
  errorMsg: string | null;
} {
  const r = resp as Record<string, unknown> | null;
  if (r?.success === false) {
    return { job: null, errorMsg: typeof r.result === "string" ? r.result : "Job not found" };
  }
  if (r && typeof r === "object" && "result" in r && r.result && typeof r.result === "object") {
    return { job: r.result as Record<string, unknown>, errorMsg: null };
  }
  return { job: (r as Record<string, unknown> | null) ?? null, errorMsg: null };
}

interface DetailSections {
  readonly gridFields: Array<{ label: string; value: string }>;
  readonly fullFields: Array<{ label: string; value: string }>;
  readonly dynamicHeaders: Array<{ field: string; value: string }>;
  readonly attachments: Array<{ name: string; url: string }>;
}

function collectDetailFields(job: Record<string, unknown>): {
  gridFields: Array<{ label: string; value: string }>;
  fullFields: Array<{ label: string; value: string }>;
} {
  const gridFields: Array<{ label: string; value: string }> = [];
  const fullFields: Array<{ label: string; value: string }> = [];
  for (const fm of DETAIL_FIELDS) {
    let val = pickField(job, fm.keys);
    if (!val) continue;
    if (fm.date || fm.dateOnly) {
      const t = Date.parse(val);
      if (!Number.isNaN(t)) {
        val = (fm.dateOnly ? DETAIL_DATE_ONLY_FMT : DETAIL_DATE_FMT).format(new Date(t));
      }
    }
    (fm.full ? fullFields : gridFields).push({ label: fm.label, value: val });
  }
  return { gridFields, fullFields };
}

function collectDynamicHeaders(job: Record<string, unknown>): Array<{ field: string; value: string }> {
  const headers: Array<{ field: string; value: string }> = [];
  if (!Array.isArray(job.headers)) return headers;
  for (const h of job.headers as Array<Record<string, unknown>>) {
    const field = pickField(h, ["field", "FIELD", "name"]);
    const value = pickField(h, ["value", "VALUE"]);
    // JOB_TITLE is already shown as the detail heading (AIVA-6987) — don't
    // repeat it under "Additional Details".
    if (field && value && field.toUpperCase() !== "JOB_TITLE") headers.push({ field, value });
  }
  return headers;
}

function collectDetailAttachments(
  job: Record<string, unknown>,
): Array<{ name: string; url: string }> {
  if (!Array.isArray(job.attachments)) return [];
  return (job.attachments as Array<Record<string, unknown>>).map((a) => ({
    name: pickField(a, ["fileName", "name", "FILENAME", "FILE_NAME", "file_name", "filE_NAME"]) || "Attachment",
    url: pickField(a, ["url", "URL", "filE_LOCATION", "FILE_LOCATION"]),
  }));
}

/** Build the detail-view sections (fields, dynamic headers, attachments) from a job. */
function buildDetailSections(job: Record<string, unknown> | null): DetailSections {
  if (!job) return { gridFields: [], fullFields: [], dynamicHeaders: [], attachments: [] };
  const { gridFields, fullFields } = collectDetailFields(job);
  return {
    gridFields,
    fullFields,
    dynamicHeaders: collectDynamicHeaders(job),
    attachments: collectDetailAttachments(job),
  };
}

/** Fetch a single job's detail, unwrapping the Engage response envelope. */
function useEngageJob(site: string, jobNo: string) {
  const client = useEngageClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setJob(null);
    client
      .getJob(site, jobNo)
      .then((resp) => {
        if (!alive) return;
        const { job: j, errorMsg } = unwrapJob(resp);
        if (errorMsg) setError(errorMsg);
        else setJob(j);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : "Failed to load job");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [client, site, jobNo]);

  return { loading, error, job };
}

/**
 * Surface a job's attachments from the OneDrive folder the add-in staged them
 * into (`AiVA/Engage/{jobNo}`). The Engage `Requests/Get` response carries no
 * attachment list, so we read the deterministic folder directly via Graph
 * (AIVA-7247). Best-effort: no Graph token, a missing folder, or any Graph
 * error yields an empty list and the Attachments section simply stays hidden.
 *
 * `openFile` opens a staged file read-only, exactly as the Knowledge Base does
 * (AIVA-7093): a Graph **preview** URL minted on click is the only surface that
 * reliably renders every file type view-only. A raw `webUrl` downloads the file
 * (and is caught by MCAS as a "Blocked_…" download), and the preview URL is
 * short-lived so it can't be a stored href — hence open-on-click, not an <a>.
 * Fails closed: a failed preview never falls back to an editable/download link.
 */
function useJobDriveAttachments(jobNo: string): {
  attachments: Array<{ name: string; id: string }>;
  loading: boolean;
  openFile: (item: { id: string; name: string }) => Promise<void>;
} {
  const { graphToken, refreshGraphToken } = useAuthState();
  const [attachments, setAttachments] = useState<Array<{ name: string; id: string }>>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;
    setAttachments([]);
    setLoading(true);
    void (async () => {
      try {
        const token = graphToken ?? (await refreshGraphToken());
        if (!token) return;
        let items;
        try {
          items = await fetchDriveFolderChildrenByPath(
            token,
            "/me/drive",
            `AiVA/Engage/${jobNo}`,
            refreshGraphToken,
          );
        } catch {
          return; // best-effort — leave the section hidden on failure
        }
        // Show real attachments only — hide the `.eml` copy of the source email
        // that the submit path also stages into this folder (AIVA-7247).
        const files = items
          .filter((it) => it.file != null && !/\.eml$/i.test(it.name))
          .map((it) => ({ name: it.name, id: it.id }));
        if (alive) setAttachments(files);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobNo, graphToken, refreshGraphToken]);

  const openFile = useCallback(
    async (item: { id: string; name: string }) => {
      const token = graphToken ?? (await refreshGraphToken());
      if (!token) return;
      try {
        const previewUrl = await fetchItemPreviewUrl(token, "/me/drive", item.id, refreshGraphToken);
        // Only a real open counts. A falsy previewUrl means Graph returned no
        // preview link — nothing opened, so logging a success there would
        // inflate the rate with non-events.
        if (previewUrl) {
          window.open(previewUrl, "_blank", "noopener,noreferrer");
          logConnectorSuccess("engage", "open-file");
        }
      } catch (e) {
        logConnectorFailure("engage", e, "open-file");
      }
    },
    [graphToken, refreshGraphToken],
  );

  return { attachments, loading, openFile };
}

function RequestDetailView({
  site,
  jobNo,
  fallbackTitle,
  onBack,
}: {
  readonly site: string;
  readonly jobNo: string;
  readonly fallbackTitle: string;
  readonly onBack: () => void;
}) {
  const { loading, error, job } = useEngageJob(site, jobNo);

  return (
    <div className={styles.card}>
      <button type="button" className={styles.detailBack} onClick={onBack}>
        ← Back to requests
      </button>

      {loading && <div className={styles.loading}>Loading job #{jobNo}…</div>}
      {error && <p className={styles.error}>Failed to load job #{jobNo}: {error}</p>}

      {job && !loading && (
        <RequestDetailBody jobNo={jobNo} job={job} fallbackTitle={fallbackTitle} />
      )}
    </div>
  );
}

function RequestDetailBody({
  jobNo,
  job,
  fallbackTitle,
}: {
  readonly jobNo: string;
  readonly job: Record<string, unknown>;
  readonly fallbackTitle: string;
}) {
  // GetJob does not return the job title for most job types (it lives only in
  // the active-list feed), so prefer any title the job object does carry, then
  // fall back to the list row's title, then "Untitled" (AIVA-6987).
  const title = readJobTitle(job) || fallbackTitle || "Untitled";
  const status = pickField(job, ["status", "STATUS", "joB_STATUS"]);
  const { gridFields, fullFields, dynamicHeaders } = buildDetailSections(job);
  // Engage's Get response has no attachment list, so pull them from the OneDrive
  // folder the add-in staged them into (AIVA-7247).
  const { attachments, loading: attachmentsLoading, openFile } = useJobDriveAttachments(jobNo);
  const hasFields = gridFields.length > 0 || fullFields.length > 0 || dynamicHeaders.length > 0;

  return (
    <>
      <div className={styles.detailHead}>
        <span className={styles.reqJobNo}>#{jobNo}</span>
        {status && (
          <span className={`${styles.reqStatus} ${statusColorClass(status)}`}>
            {formatStatus(status)}
          </span>
        )}
      </div>
      <h3 className={styles.detailTitle}>{title}</h3>

      {gridFields.length > 0 && (
        <div className={styles.detailGrid}>
          {gridFields.map((f) => (
            <DetailField key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
      )}
      {fullFields.map((f) => (
        <DetailField key={f.label} label={f.label} value={f.value} />
      ))}

      {dynamicHeaders.length > 0 && (
        <div className={styles.detailExtra}>
          <p className={styles.label}>Additional Details</p>
          {dynamicHeaders.map((h) => (
            <DetailField key={h.field} label={h.field} value={h.value} />
          ))}
        </div>
      )}

      <div>
        <p className={styles.label}>Attachments</p>
        <AttachmentsList loading={attachmentsLoading} attachments={attachments} openFile={openFile} />
      </div>

      {!hasFields && <p className={styles.emptyMsg}>No additional details available</p>}
    </>
  );
}

/** Loading / empty / list rendering for a job's attachments (AIVA-6989). */
function AttachmentsList({
  loading,
  attachments,
  openFile,
}: {
  readonly loading: boolean;
  readonly attachments: ReadonlyArray<{ name: string; id: string }>;
  readonly openFile: (item: { id: string; name: string }) => Promise<void>;
}) {
  if (loading) return <p className={styles.attachItem}>Loading attachments…</p>;
  if (attachments.length === 0) return <p className={styles.attachItem}>No attachments</p>;
  return (
    <>
      {attachments.map((a, i) => (
        <div key={`${a.name}-${i}`} className={styles.attachItem}>
          <span className={styles.attachIcon} aria-hidden="true">
            <PaperclipIcon />
          </span>{" "}
          <button type="button" className={styles.attachLink} onClick={() => void openFile(a)}>
            {a.name}
          </button>
        </div>
      ))}
    </>
  );
}

function DetailField({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className={styles.detailField}>
      <div className={styles.label}>{label}</div>
      <div className={styles.detailValue}>{value}</div>
    </div>
  );
}
